import { z } from 'zod';
import { paginationQuerySchema } from '../common/envelope.js';
import { addressSchema } from '../common/address.js';
export const leadPrioritySchema = z.enum(['low', 'normal', 'high']);
export const leadTemperatureSchema = z.enum(['cold', 'warm', 'hot']);
export const createLeadSchema = z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    email: z.string().trim().toLowerCase().email().optional(),
    phone: z.string().trim().max(40).optional(),
    companyName: z.string().trim().max(200).optional(),
    jobTitle: z.string().trim().max(150).optional(),
    website: z.string().trim().url().optional(),
    address: addressSchema.optional(),
    statusId: z.string().uuid().optional(),
    sourceId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
    priority: leadPrioritySchema.default('normal'),
    temperature: leadTemperatureSchema.optional(),
    tagIds: z.array(z.string().uuid()).max(50).default([]),
    customFields: z.record(z.string(), z.unknown()).default({}),
    /** When the caller has already resolved a duplicate warning. */
    confirmDuplicate: z.boolean().default(false),
});
export const updateLeadSchema = createLeadSchema.partial().extend({
    version: z.number().int().nonnegative(),
    /** Read everywhere (Leads list's "next follow-up" column, the mobile
     * dashboard/leads screens, Lead detail's sticky action bar) but never
     * settable until now — every write path stopped short of this one
     * column. `null` clears it. */
    nextFollowUpAt: z.string().datetime().nullable().optional(),
});
export const assignLeadSchema = z.object({
    ownerId: z.string().uuid().nullable(),
});
/** docs/architecture/08-v1-scope.md § "Deduplication" — merges `duplicateId`
 * into the lead at `:id` (the "primary"/survivor); the duplicate is
 * soft-deleted and its history (tasks, notes, activities, tags) re-linked
 * to the primary. */
export const mergeLeadSchema = z.object({
    duplicateId: z.string().uuid(),
});
export const listLeadsQuerySchema = paginationQuerySchema.extend({
    statusId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
    /** True to show only leads with no owner — mutually exclusive with
     * `ownerId` in practice (the UI never sets both at once). */
    unassigned: z.coerce.boolean().optional(),
    sourceId: z.string().uuid().optional(),
    temperature: leadTemperatureSchema.optional(),
    /** Design's "Follow-ups today" built-in tab (design/orion-crm-design/
     * rendered-html/LeadsList.html) — leads with a next-follow-up at or
     * before this instant. */
    nextFollowUpBefore: z.string().datetime().optional(),
    savedViewId: z.string().uuid().optional(),
});
/** Lightweight status/source reference lists for the list page's filter
 * dropdowns — `admin/lead-config`'s endpoints return the same underlying
 * rows but are gated behind `admin.lead_config.configure`, which most
 * reps filtering their own leads don't have. Gated by `crm.leads.view`
 * instead (the same permission needed to see the list at all). */
export const leadStatusOptionSchema = z.object({ id: z.string().uuid(), name: z.string(), color: z.string(), category: z.string() });
export const leadSourceOptionSchema = z.object({ id: z.string().uuid(), name: z.string() });
/** The 5 built-in tabs at the top of the Leads list — real counts, not the
 * user's customizable saved views (which stay a separate, additional
 * affordance alongside these). */
export const leadCountsResponseSchema = z.object({
    all: z.number().int(),
    mine: z.number().int(),
    hot: z.number().int(),
    followUpsToday: z.number().int(),
    unassigned: z.number().int(),
});
export const leadResponseSchema = z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    fullName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    companyName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    website: z.string().nullable(),
    address: addressSchema.nullable(),
    status: z.object({ id: z.string().uuid(), name: z.string(), color: z.string(), category: z.string() }),
    source: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    /** Set only when `companyName` was auto-linked to a real Company record
     * (today: Integrations/MCP ingestion only, `IntegrationsService#upsertLead`)
     * — null for a lead created through the regular "New Lead" form, where
     * `companyName` stays free text with nothing to link to yet. */
    company: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    owner: z.object({ id: z.string().uuid(), displayName: z.string() }).nullable(),
    score: z.number().int().nullable(),
    priority: leadPrioritySchema,
    temperature: leadTemperatureSchema.nullable(),
    tags: z.array(z.object({ id: z.string().uuid(), name: z.string(), color: z.string() })),
    lastContactedAt: z.string().datetime().nullable(),
    nextFollowUpAt: z.string().datetime().nullable(),
    convertedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    version: z.number().int(),
});
/** `POST /leads`'s real response shape — a manual "New Lead" form
 * submission goes through the same AI qualification gate every other
 * lead-creation path does. `status: 'created'` is the normal case
 * (matches prior behavior exactly, including when AI qualification is
 * disabled tenant-wide); the other three mean no Lead exists yet —
 * `qualificationId` points at the `AiLeadQualification` row (AI Lead
 * Review) explaining why. */
export const createLeadResultSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('created'), lead: leadResponseSchema }),
    z.object({ status: z.enum(['needs_review', 'rejected', 'error']), qualificationId: z.string().uuid().nullable() }),
]);
/** A potential-duplicate hit surfaced by the dedupe check
 * (docs/architecture/03-database.md § indexing — duplicate detection). */
export const duplicateCandidateSchema = z.object({
    leadId: z.string().uuid(),
    fullName: z.string(),
    email: z.string().nullable(),
    companyName: z.string().nullable(),
    ownerName: z.string().nullable(),
    statusName: z.string(),
    matchedOn: z.array(z.enum(['email', 'phone', 'name_company'])),
});
//# sourceMappingURL=schemas.js.map